import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { apiPost, apiGet } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, Loader2 } from "lucide-react";
import { useUnsavedChanges } from "@/hooks/use-unsaved-changes";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { useGoBack } from "@/hooks/use-go-back";

export const Route = createFileRoute("/_authenticated/incidents/report")({
  component: ReportIncidentPage,
});

const INCIDENT_TYPES = [
  { value: "injury", label: "Injury" },
  { value: "near_miss", label: "Near Miss" },
  { value: "property_damage", label: "Property Damage" },
  { value: "environmental", label: "Environmental" },
  { value: "vehicle", label: "Vehicle" },
  { value: "other", label: "Other" },
] as const;

const SEVERITY_OPTIONS = [
  { value: "minor", label: "Minor" },
  { value: "moderate", label: "Moderate" },
  { value: "serious", label: "Serious" },
  { value: "critical", label: "Critical" },
] as const;

function ReportIncidentPage() {
  const navigate = useNavigate();
  const goBack = useGoBack({ to: "/incidents" });
  const queryClient = useQueryClient();

  const [incidentType, setIncidentType] = useState("near_miss");
  const [severity, setSeverity] = useState("minor");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [time, setTime] = useState("");
  const [location, setLocation] = useState("");
  const [description, setDescription] = useState("");
  const [immediateActions, setImmediateActions] = useState("");
  const [projectId, setProjectId] = useState("");

  const isDirty = !!(description || location || immediateActions);
  useUnsavedChanges(isDirty);

  const { data: projectsData } = useQuery({
    queryKey: ["projects", "active"],
    queryFn: () =>
      apiGet<{ data: Array<{ id: string; name: string }> }>(
        "/api/projects?status=active"
      ),
  });

  const projects = projectsData?.data || [];

  const createMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiPost<{ data: { id: string } }>("/api/incidents", body),
    onSuccess: (res) => {
      toast.success("Incident reported");
      queryClient.invalidateQueries({ queryKey: ["incidents"] });
      navigate({
        to: "/incidents/$incidentId",
        params: { incidentId: res.data.id },
      });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <div className="space-y-4 max-w-lg mx-auto">
      <div className="flex items-center gap-2 pt-2">
        <Button
          variant="ghost"
          size="sm"
          className="p-1"
          onClick={goBack} aria-label="Back"
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-2xl font-bold text-text-primary">
          Report Incident
        </h1>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Type *</Label>
            <select
              value={incidentType}
              onChange={(e) => setIncidentType(e.target.value)}
              className="flex h-12 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              {INCIDENT_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label>Severity *</Label>
            <select
              value={severity}
              onChange={(e) => setSeverity(e.target.value)}
              className="flex h-12 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              {SEVERITY_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Date *</Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              max={new Date().toISOString().split("T")[0]}
              className="h-12"
            />
          </div>
          <div className="space-y-2">
            <Label>Time</Label>
            <Input
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className="h-12"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label>Project</Label>
          <SearchableSelect
            className="h-12"
            value={projectId}
            onChange={setProjectId}
            allLabel="None"
            allValue=""
            options={projects.map((p) => ({ value: p.id, label: p.name }))}
          />
        </div>

        <div className="space-y-2">
          <Label>Location</Label>
          <Input
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="Where did it happen?"
            maxLength={200}
            className="h-12"
          />
        </div>

        <div className="space-y-2">
          <Label>Description *</Label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What happened?"
            rows={4}
            maxLength={2000}
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-base"
          />
        </div>

        <div className="space-y-2">
          <Label>Immediate Actions Taken</Label>
          <textarea
            value={immediateActions}
            onChange={(e) => setImmediateActions(e.target.value)}
            placeholder="What was done right away?"
            rows={2}
            maxLength={2000}
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-base"
          />
        </div>

        <Button
          className="w-full h-12 bg-paris-red hover:bg-paris-red-dark"
          disabled={!description.trim() || !date || createMutation.isPending}
          onClick={() =>
            createMutation.mutate({
              incidentType,
              severity,
              date,
              time: time || undefined,
              location: location.trim() || undefined,
              description: description.trim(),
              immediateActions: immediateActions.trim() || undefined,
              projectId: projectId || undefined,
            })
          }
        >
          {createMutation.isPending ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            "Submit Report"
          )}
        </Button>
      </div>
    </div>
  );
}
